import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar, backLink } from "../components/common.js";
import { toast, openModal, spinner } from "../components/ui.js";
import { decodeWorkout } from "../lib/share.js";
import { loadLibrary } from "../store.js";
import { parseDocument, indexLibrary, matchExercise } from "../lib/importDoc.js";
import { openExercisePicker } from "../components/ExercisePicker.js";

// Import screen: two sources — a compact share code (single workout, exact-name lookup on the
// server) or a Markdown document (a Kashtu export or anything written in the same shape, incl.
// whole programs). Documents are parsed + matched client-side so the user can fix fuzzy or
// missing exercise matches before anything is created.
export default function Import() {
  const root = h("div", { class: "screen" });
  const params = new URLSearchParams(location.search);
  const codeFromUrl = params.get("code") || "";
  let mode = params.get("mode") === "doc" && !codeFromUrl ? "doc" : "code";
  let plan = null; // parsed + matched document, when in doc mode

  render(codeFromUrl);

  function render(code) {
    setChildren(root,
      backLink("Workouts", "/workouts"),
      topbar("Import", { sub: "Bring in a shared workout or a written program" }),
      h("div", { class: "btn-row", style: { marginBottom: "14px" } },
        tabBtn("Share code", "code"),
        tabBtn("Document", "doc")
      ),
      mode === "code" ? codeView(code ?? "") : docView()
    );
  }

  function tabBtn(label, m) {
    return h("button", { class: ["btn grow", mode === m ? "btn-primary" : "btn-ghost"], onClick: () => { mode = m; render(""); } }, label);
  }

  // ---------------------------------------------------------------------------
  // Share-code mode (unchanged behavior)
  // ---------------------------------------------------------------------------

  function codeView(code) {
    const preview = code ? decodeWorkout(code) : null;
    if (!code) {
      return h("div", { class: "field" },
        h("label", null, "Paste a share code"),
        h("textarea", { class: "textarea", id: "code-in", placeholder: "Paste the workout share code here…", rows: 4 }),
        h("button", { class: "btn btn-primary btn-block mt", onClick: () => render(document.querySelector("#code-in").value.trim()) }, "Preview")
      );
    }
    if (!preview || !preview.exercises) {
      return h("div", { class: "card center" },
        h("p", { class: "dim" }, "That code couldn't be read. Check you copied all of it."),
        h("button", { class: "btn btn-ghost mt", onClick: () => render("") }, "Try another code")
      );
    }
    return h("div", null,
      h("div", { class: "card" },
        h("div", { style: { fontWeight: 700, fontSize: "1.15rem" } }, preview.name || "Shared workout"),
        h("div", { class: "dim", style: { margin: "2px 0 10px" } }, `${preview.exercises.length} exercises`),
        ...preview.exercises.map((e) =>
          h("div", { class: "row-between", style: { padding: "5px 0" } },
            h("span", null, e.exercise_name),
            h("span", { class: "dim" }, repLabel(e))
          )
        )
      ),
      h("button", { class: "btn btn-primary btn-block btn-lg mt", onClick: () => addCode(code) }, icon("plus", { size: 20 }), "Add to my workouts"),
      h("button", { class: "btn btn-ghost btn-block mt", onClick: () => render("") }, "Paste a different code")
    );
  }

  function repLabel(e) {
    const sets = e.target_sets ?? 3;
    if (e.target_reps_min == null) return `${sets} sets`;
    const range = e.target_reps_max && e.target_reps_max !== e.target_reps_min ? `${e.target_reps_min}-${e.target_reps_max}` : `${e.target_reps_min}`;
    return `${sets}×${range}`;
  }

  async function addCode(code) {
    try {
      const w = await api.importWorkout(code);
      if (w.imported_missing) toast(`Imported (${w.imported_missing} exercise(s) not found in this library)`, "warn");
      else toast("Workout imported!", "success");
      navigate("/workouts");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  // ---------------------------------------------------------------------------
  // Document mode
  // ---------------------------------------------------------------------------

  function docView() {
    if (plan) return docPreview();
    const fileInput = h("input", {
      type: "file", accept: ".md,.markdown,.txt,text/plain,text/markdown", style: { display: "none" },
      onchange: async (e) => { const f = e.target.files[0]; if (f) buildPlan(await f.text()); },
    });
    return h("div", null,
      h("div", { class: "field" },
        h("label", null, "Paste a program or workout document"),
        h("textarea", { class: "textarea", id: "doc-in", rows: 8, placeholder: "# My program\n\n## Day 1: Lower\n\n### 1. Barbell Hip Thrust\n- Target: 3 × 8–12 reps\n- Rest: 2:30\n…" }),
        h("button", { class: "btn btn-primary btn-block mt", onClick: () => buildPlan(document.querySelector("#doc-in").value) }, "Preview"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => fileInput.click() }, icon("download", { size: 18 }), "Choose a file…"),
        fileInput
      ),
      h("p", { class: "dim", style: { fontSize: ".82rem", marginTop: "10px" } },
        "Works with documents exported from Kashtu — or any plan written in the same Markdown shape (day headings, numbered exercises, Target/Rest bullets). Exercise names are matched against the library; you can fix any that don't match before importing.")
    );
  }

  async function buildPlan(text) {
    const doc = parseDocument(text);
    if (!doc) { toast("Couldn't find any workouts in that text — expected Markdown with day/exercise headings.", "error"); return; }
    setChildren(root, backLink("Workouts", "/workouts"), topbar("Import"), spinner());
    let library;
    try {
      library = await loadLibrary();
    } catch {
      toast("Exercise library unavailable offline — reconnect to import.", "error");
      plan = null;
      render("");
      return;
    }
    const index = indexLibrary(library);
    const wrap = (ex) => {
      const r = matchExercise(index, ex.name, { equipment: ex.equipment });
      return { parsed: ex, match: r.match, exact: r.exact, suggestions: r.suggestions, skipped: false };
    };
    plan = {
      doc,
      importing: false,
      days: doc.kind === "program" ? doc.days.map((d) => ({ ...d, items: d.exercises.map(wrap) })) : null,
      items: doc.kind === "workout" ? doc.exercises.map(wrap) : null,
    };
    render("");
  }

  const allItems = () => (plan.items || plan.days.flatMap((d) => d.items));
  const unresolved = () => allItems().filter((it) => !it.skipped && !it.match);

  function docPreview() {
    const { doc } = plan;
    const missing = unresolved().length;
    const workoutCount = doc.kind === "program" ? new Set(plan.days.filter((d) => !d.is_rest).map((d) => d.workout_name.toLowerCase())).size : 1;
    const summary = doc.kind === "program"
      ? `${plan.days.length}-day program · ${workoutCount} workout${workoutCount === 1 ? "" : "s"} · ${allItems().length} exercises`
      : `Workout · ${allItems().length} exercises`;

    const importLabel = plan.importing
      ? "Importing…"
      : missing
      ? `Match ${missing} exercise${missing === 1 ? "" : "s"} to import`
      : doc.kind === "program" ? "Import program" : "Import workout";

    return h("div", null,
      h("div", { class: "card" },
        h("div", { style: { fontWeight: 700, fontSize: "1.15rem" } }, doc.name),
        h("div", { class: "dim", style: { marginTop: "2px", fontSize: ".85rem" } }, summary),
        doc.description ? h("div", { class: "dim", style: { marginTop: "6px", fontSize: ".82rem" } }, doc.description) : null
      ),
      missing
        ? h("div", { class: "card", style: { borderColor: "var(--warning, #d97706)" } },
            h("div", { style: { fontWeight: 600 } }, `${missing} exercise${missing === 1 ? " isn't" : "s aren't"} in the library yet`),
            h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, "Tap a highlighted exercise to pick the closest match, or skip it."))
        : null,
      doc.kind === "program"
        ? h("div", null, ...plan.days.map(dayCard))
        : h("div", { class: "card" }, ...plan.items.map(itemRow)),
      h("button", {
        class: "btn btn-primary btn-block btn-lg mt",
        disabled: !!(missing || plan.importing || !anyImportable()),
        onClick: doImport,
      }, icon("check", { size: 20 }), importLabel),
      h("button", { class: "btn btn-ghost btn-block mt", disabled: plan.importing, onClick: () => { plan = null; render(""); } }, "Start over")
    );
  }

  const anyImportable = () => allItems().some((it) => !it.skipped && it.match);

  function dayCard(d, i) {
    if (d.is_rest) {
      return h("div", { class: "card", style: { opacity: ".72" } },
        h("div", { class: "row-between" },
          h("span", { style: { fontWeight: 600 } }, `Day ${i + 1} · ${d.label || "Rest"}`),
          h("span", { class: "dim", style: { fontSize: ".82rem" } }, "Rest day"))
      );
    }
    return h("div", { class: "card" },
      h("div", { style: { fontWeight: 700, marginBottom: "6px" } }, `Day ${i + 1} · ${d.workout_name}`),
      ...d.items.map(itemRow)
    );
  }

  function itemRow(it) {
    const p = it.parsed;
    const target = [
      p.target_sets ? `${p.target_sets}×` : "",
      p.target_reps_min != null ? (p.target_reps_max !== p.target_reps_min ? `${p.target_reps_min}–${p.target_reps_max}` : `${p.target_reps_min}`) : "",
    ].join("") || null;

    let status, detail = null;
    if (it.skipped) {
      status = h("span", { class: "dim" }, icon("x", { size: 16 }));
    } else if (!it.match) {
      status = h("span", { style: { color: "var(--danger)" } }, icon("search", { size: 16 }));
      detail = h("span", { style: { color: "var(--danger)", fontSize: ".78rem" } }, "tap to match");
    } else if (!it.exact || it.match.exercise_name !== p.name) {
      status = h("span", { style: { color: "var(--success)" } }, icon("check", { size: 16 }));
      detail = h("span", { class: "dim", style: { fontSize: ".78rem" } }, `→ ${it.match.exercise_name}`);
    } else {
      status = h("span", { style: { color: "var(--success)" } }, icon("check", { size: 16 }));
    }

    return h("div", { class: "hstack card-tap", style: { padding: "6px 0", gap: "8px", alignItems: "baseline" }, onClick: () => resolveModal(it) },
      status,
      h("div", { class: "grow", style: { minWidth: 0 } },
        h("span", { style: it.skipped ? { textDecoration: "line-through", opacity: ".6" } : null }, p.name),
        detail ? h("div", null, detail) : null
      ),
      target ? h("span", { class: "dim", style: { fontSize: ".82rem", flexShrink: 0 } }, target) : null
    );
  }

  function resolveModal(it) {
    const m = openModal({
      title: it.parsed.name,
      content: h("div", null,
        h("p", { class: "dim", style: { fontSize: ".85rem" } },
          it.match ? ["Currently matched to ", h("b", null, it.match.exercise_name), "."] : "No library match found — pick one below or search."),
        it.suggestions.length
          ? h("div", { class: "mt" },
              h("div", { class: "dim", style: { fontSize: ".78rem", fontWeight: 700, letterSpacing: ".04em", marginBottom: "6px" } }, "SUGGESTIONS"),
              ...it.suggestions.map((s) =>
                h("button", { class: "btn btn-ghost btn-block", style: { justifyContent: "flex-start" }, onClick: () => { it.match = s.row; it.exact = false; it.skipped = false; m.close(); render(""); } },
                  h("span", { class: "grow", style: { textAlign: "left" } }, s.row.exercise_name),
                  h("span", { class: "dim", style: { fontSize: ".78rem" } }, s.row.equipment || ""))
              ))
          : null,
        h("button", { class: "btn btn-primary btn-block mt", onClick: () => { m.close(); searchFor(it); } }, icon("search", { size: 18 }), "Search the library"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { it.skipped = !it.skipped; m.close(); render(""); } },
          it.skipped ? "Include this exercise" : "Skip this exercise")
      ),
    });
  }

  function searchFor(it) {
    openExercisePicker({
      title: `Match "${it.parsed.name}"`,
      onPick: (ex) => { it.match = ex; it.exact = false; it.skipped = false; render(""); return true; },
    });
  }

  // Build the createWorkout exercises payload from resolved (non-skipped) items.
  function exercisesPayload(items) {
    return items.filter((it) => !it.skipped && it.match).map((it, i) => ({
      library_exercise_id: it.match.id,
      position: i,
      target_sets: it.parsed.target_sets ?? 3,
      target_reps_min: it.parsed.target_reps_min,
      target_reps_max: it.parsed.target_reps_max,
      target_weight_kg: it.parsed.target_weight_kg,
      target_duration_seconds: it.parsed.target_duration_seconds,
      target_distance_m: it.parsed.target_distance_m,
      rest_seconds: it.parsed.rest_seconds,
      notes: it.parsed.notes,
      superset_group: it.parsed.superset_group,
    }));
  }

  async function doImport() {
    plan.importing = true;
    render("");
    try {
      if (plan.doc.kind === "workout") {
        await api.createWorkout({ name: plan.doc.name, notes: plan.doc.notes, exercises: exercisesPayload(plan.items) });
        toast("Workout imported!", "success");
        navigate("/workouts");
        return;
      }
      // Program: create one workout per distinct day title (repeated slots reuse the same
      // workout), then the program itself, then the day schedule.
      const byName = new Map();
      for (const d of plan.days) {
        if (d.is_rest) continue;
        const key = d.workout_name.toLowerCase();
        if (!byName.has(key)) byName.set(key, d);
      }
      const created = new Map();
      for (const [key, d] of byName) {
        const exercises = exercisesPayload(d.items);
        if (!exercises.length) { created.set(key, null); continue; } // everything skipped → rest slot
        const w = await api.createWorkout({ name: d.workout_name, exercises });
        created.set(key, w.id);
      }
      const prog = await api.createProgram({ name: plan.doc.name, description: plan.doc.description });
      await api.setProgramDays(prog.id, plan.days.map((d) => ({
        workout_id: d.is_rest ? null : created.get(d.workout_name.toLowerCase()),
        label: d.is_rest ? (d.label || "Rest") : (d.label || null),
      })));
      toast(`Program "${plan.doc.name}" imported`, "success");
      navigate("/programs");
    } catch (e) {
      plan.importing = false;
      render("");
      toast(e.message, "error");
    }
  }

  return root;
}
